import { Check, Edit3, Eye, PackagePlus, Plus, RotateCcw, Trash2, Wrench } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, apiRequest } from "../api";
import { useAuth } from "../auth";
import { ErrorState, LoadingState } from "../components/PageState";
import { matchesQuickSearch, QuickSearch } from "../components/QuickSearch";
import { SearchSelect } from "../components/SearchSelect";
import { formatDate, formatMoney, formatNumber, Message, Modal, PageHeader, StatusBadge, Tabs, today } from "../components/Ui";
import type { DocumentStatus, InventoryDocumentRow, InventoryDocumentType, ItemListRow, UomRow } from "../types";

type MainTab = "Assets" | "SpareParts";
type SpareTab = "Catalog" | "Receipt" | "Issue" | "Stock";

type EquipmentAsset = {
  id: string;
  code: string;
  name: string;
  specificationSymbol?: string | null;
  serialNumber?: string | null;
  location?: string | null;
  notes?: string | null;
  isActive: boolean;
};

type EquipmentFoundation = {
  uoms: UomRow[];
  warehouse?: { id: string; code: string; name: string } | null;
  suppliers: Array<{ id: string; code: string; name: string; partnerType: string }>;
};

type StockItem = {
  itemId: string;
  itemCode: string;
  itemName: string;
  uom: string;
  balance: number;
  minimumStockQuantity?: number | null;
  isBelowMinimum: boolean;
  warehouseNames: string[];
};

type StockResponse = { items: StockItem[] };

type DocumentLine = {
  clientKey: string;
  itemId: string;
  uomId: string;
  quantity: number;
  unitPrice: string;
  vatPercent: string;
};

type EquipmentDocumentDetail = {
  id: string;
  number: string;
  documentType: InventoryDocumentType;
  documentDate: string;
  warehouseId: string;
  warehouseName?: string | null;
  partnerId?: string | null;
  partnerName?: string | null;
  equipmentId?: string | null;
  equipment?: { id: string; code: string; name: string } | null;
  equipmentPurpose?: string | null;
  recipient?: string | null;
  status: DocumentStatus;
  totalAmount: number;
  notes?: string | null;
  lines: Array<{
    id: string;
    lineNo: number;
    itemId: string;
    itemCode: string;
    itemName: string;
    uomId: string;
    uomName: string;
    quantity: number;
    unitPrice?: number | null;
    vatPercent?: number | null;
    lineTotal?: number | null;
  }>;
};

const purposeOptions = {
  Receipt: [
    { value: "Purchase", label: "Mua linh kiện" },
    { value: "Returned", label: "Nhập hoàn trả" },
    { value: "Recovered", label: "Thu hồi về kho" },
    { value: "Adjustment", label: "Điều chỉnh tăng" },
    { value: "Other", label: "Nhập khác" }
  ],
  Issue: [
    { value: "Repair", label: "Sửa chữa thiết bị" },
    { value: "Maintenance", label: "Bảo trì thiết bị" },
    { value: "Use", label: "Cấp phát sử dụng" },
    { value: "SupplierReturn", label: "Trả nhà cung cấp" },
    { value: "Disposal", label: "Thanh lý / loại bỏ" },
    { value: "Other", label: "Xuất khác" }
  ]
};

const purposeLabel = (type: InventoryDocumentType, value?: string | null) =>
  purposeOptions[type].find((option) => option.value === value)?.label ?? value ?? "—";

let lineSequence = 0;
const blankLine = (): DocumentLine => ({
  clientKey: `spare-line-${Date.now()}-${++lineSequence}`,
  itemId: "",
  uomId: "",
  quantity: 1,
  unitPrice: "",
  vatPercent: ""
});

const blankAsset = () => ({
  code: "", name: "", specificationSymbol: "", serialNumber: "", location: "", notes: "", isActive: true
});

const blankPart = (uomId = "") => ({
  code: "", name: "", baseUomId: uomId, specificationSymbol: "", trackMinimumStock: false,
  minimumStockQuantity: null as number | null, isActive: true
});

export function EquipmentPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("ADMIN") === true;
  const hasPermission = (permission: string) => isAdmin || user?.permissions.includes(permission) === true;
  const [mainTab, setMainTab] = useState<MainTab>("Assets");
  const [spareTab, setSpareTab] = useState<SpareTab>("Catalog");
  const [assets, setAssets] = useState<EquipmentAsset[]>([]);
  const [parts, setParts] = useState<ItemListRow[]>([]);
  const [documents, setDocuments] = useState<InventoryDocumentRow[]>([]);
  const [stock, setStock] = useState<StockItem[]>([]);
  const [foundation, setFoundation] = useState<EquipmentFoundation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [assetForm, setAssetForm] = useState<ReturnType<typeof blankAsset> | null>(null);
  const [editingAssetId, setEditingAssetId] = useState("");
  const [partForm, setPartForm] = useState<ReturnType<typeof blankPart> | null>(null);
  const [editingPartId, setEditingPartId] = useState("");
  const [documentForm, setDocumentForm] = useState<{
    number: string; documentDate: string; documentType: InventoryDocumentType; partnerId: string;
    equipmentId: string; equipmentPurpose: string; recipient: string; notes: string; lines: DocumentLine[];
  } | null>(null);
  const [editingDocumentId, setEditingDocumentId] = useState("");
  const [detail, setDetail] = useState<EquipmentDocumentDetail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [assetData, foundationData, partData, receiptData, issueData, stockData] = await Promise.all([
        apiRequest<EquipmentAsset[]>("/api/equipment/assets"),
        apiRequest<EquipmentFoundation>("/api/equipment/foundation"),
        apiRequest<ItemListRow[]>("/api/items?type=SparePart&scope=equipment"),
        apiRequest<InventoryDocumentRow[]>("/api/inventory/documents?type=Receipt&scope=equipment"),
        apiRequest<InventoryDocumentRow[]>("/api/inventory/documents?type=Issue&scope=equipment"),
        apiRequest<StockResponse>("/api/inventory/stock?itemType=SparePart&scope=equipment")
      ]);
      setAssets(assetData);
      setFoundation(foundationData);
      setParts(partData);
      setDocuments([...receiptData, ...issueData]);
      setStock(stockData.items);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không tải được dữ liệu Thiết bị và Linh kiện.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const uomOptions = useMemo(() => (foundation?.uoms ?? []).map((uom) => ({
    value: uom.id, label: `${uom.code} – ${uom.name}`, selectedLabel: uom.name, keywords: `${uom.code} ${uom.name} ${uom.symbol}`
  })), [foundation]);
  const partOptions = useMemo(() => parts.filter((part) => part.isActive).map((part) => ({
    value: part.id, label: `${part.code} – ${part.name}`, selectedLabel: part.name, keywords: `${part.code} ${part.name} ${part.specificationSymbol ?? ""}`
  })), [parts]);
  const equipmentOptions = useMemo(() => assets.filter((asset) => asset.isActive).map((asset) => ({
    value: asset.id, label: `${asset.code} – ${asset.name}`, keywords: `${asset.code} ${asset.name} ${asset.serialNumber ?? ""}`
  })), [assets]);
  const supplierOptions = useMemo(() => (foundation?.suppliers ?? []).map((supplier) => ({
    value: supplier.id, label: `${supplier.code} – ${supplier.name}`, keywords: `${supplier.code} ${supplier.name}`
  })), [foundation]);

  const visibleAssets = useMemo(() => assets.filter((row) => matchesQuickSearch(
    search, row.code, row.name, row.specificationSymbol, row.serialNumber, row.location, row.notes,
    row.isActive ? "đang dùng" : "ngừng dùng"
  )), [assets, search]);
  const visibleParts = useMemo(() => parts.filter((row) => matchesQuickSearch(
    search, row.code, row.name, row.baseUom, row.specificationSymbol, row.minimumStockQuantity
  )), [parts, search]);
  const selectedDocumentType: InventoryDocumentType = spareTab === "Issue" ? "Issue" : "Receipt";
  const visibleDocuments = useMemo(() => documents.filter((row) =>
    row.documentType === selectedDocumentType && matchesQuickSearch(
      search, row.number, row.documentDate, row.equipmentPurpose, row.recipient,
      row.equipment?.code, row.equipment?.name, row.partner?.name, row.status
    )
  ), [documents, search, selectedDocumentType]);
  const visibleStock = useMemo(() => stock.filter((row) => matchesQuickSearch(
    search, row.itemCode, row.itemName, row.uom, row.warehouseNames.join(" ")
  )), [search, stock]);

  const saveAsset = async () => {
    if (!assetForm) return;
    setError("");
    try {
      await apiRequest(editingAssetId ? `/api/equipment/assets/${editingAssetId}` : "/api/equipment/assets", {
        method: editingAssetId ? "PUT" : "POST",
        body: JSON.stringify(assetForm)
      });
      setAssetForm(null);
      setMessage(editingAssetId ? "Đã cập nhật thiết bị." : "Đã thêm thiết bị.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không lưu được thiết bị.");
    }
  };

  const savePart = async () => {
    if (!partForm) return;
    setError("");
    try {
      await apiRequest(editingPartId ? `/api/items/${editingPartId}?scope=equipment` : "/api/items?scope=equipment", {
        method: editingPartId ? "PUT" : "POST",
        body: JSON.stringify({
          ...partForm,
          itemType: "SparePart",
          isLotTracked: false,
          packagingProfile: null,
          shelfLifeDays: null,
          piecesPerBag: null,
          bagsPerBox: null,
          boxesPerCarton: null
        })
      });
      setPartForm(null);
      setMessage(editingPartId ? "Đã cập nhật danh mục linh kiện." : "Đã thêm linh kiện.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không lưu được linh kiện.");
    }
  };

  const openDocument = (documentType: InventoryDocumentType) => {
    if (!foundation?.warehouse) {
      setError("Chưa có Kho vật tư khác (KHO-VT). Hãy chạy cập nhật backend trước.");
      return;
    }
    setEditingDocumentId("");
    setDocumentForm({
      number: "", documentDate: today(), documentType, partnerId: "", equipmentId: "",
      equipmentPurpose: documentType === "Receipt" ? "Purchase" : "Repair",
      recipient: "", notes: "", lines: [blankLine()]
    });
  };

  const openEditDocument = async (id: string) => {
    setError("");
    try {
      const document = await apiRequest<EquipmentDocumentDetail>(`/api/inventory/documents/${id}?scope=equipment`);
      setEditingDocumentId(id);
      setDocumentForm({
        number: document.number,
        documentDate: document.documentDate,
        documentType: document.documentType,
        partnerId: document.partnerId ?? "",
        equipmentId: document.equipmentId ?? "",
        equipmentPurpose: document.equipmentPurpose ?? (document.documentType === "Receipt" ? "Purchase" : "Repair"),
        recipient: document.recipient ?? "",
        notes: document.notes ?? "",
        lines: document.lines.map((line) => ({
          ...blankLine(), itemId: line.itemId, uomId: line.uomId, quantity: line.quantity,
          unitPrice: line.unitPrice == null ? "" : String(line.unitPrice),
          vatPercent: line.vatPercent == null ? "" : String(line.vatPercent)
        }))
      });
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không mở được phiếu linh kiện.");
    }
  };

  const updateDocumentLine = (index: number, update: Partial<DocumentLine>) => {
    if (!documentForm) return;
    const lines = [...documentForm.lines];
    const next = { ...lines[index], ...update };
    if (update.itemId !== undefined) next.uomId = parts.find((part) => part.id === update.itemId)?.baseUomId ?? "";
    lines[index] = next;
    setDocumentForm({ ...documentForm, lines });
  };

  const saveDocument = async () => {
    if (!documentForm || !foundation?.warehouse) return;
    setError("");
    try {
      const payload = {
        number: documentForm.number || null,
        documentType: documentForm.documentType,
        reason: "Other",
        documentDate: documentForm.documentDate,
        warehouseId: foundation.warehouse.id,
        partnerId: documentForm.partnerId || null,
        relatedCommercialDocumentId: null,
        productionReportId: null,
        productionReportIds: [],
        productionPlanId: null,
        productionPlanLineId: null,
        targetProductionLotCode: null,
        equipmentId: documentForm.equipmentId || null,
        equipmentPurpose: documentForm.equipmentPurpose,
        recipient: documentForm.recipient || null,
        notes: documentForm.notes || null,
        lines: documentForm.lines.map(({ clientKey: _clientKey, ...line }) => ({
          ...line,
          lotId: null,
          productionReportLineId: null,
          productionOutputType: "FinishedProduct",
          lotCode: null,
          supplierLotCode: null,
          manufactureDate: null,
          expiryDate: null,
          unitPrice: line.unitPrice === "" ? null : Number(line.unitPrice),
          vatPercent: line.vatPercent === "" ? null : Number(line.vatPercent)
        }))
      };
      await apiRequest(
        editingDocumentId
          ? `/api/inventory/documents/${editingDocumentId}?scope=equipment`
          : "/api/inventory/documents?scope=equipment",
        { method: editingDocumentId ? "PUT" : "POST", body: JSON.stringify(payload) }
      );
      setDocumentForm(null);
      setEditingDocumentId("");
      setMessage(editingDocumentId ? "Đã cập nhật phiếu linh kiện Nháp." : "Đã tạo phiếu linh kiện Nháp.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không lưu được phiếu linh kiện.");
    }
  };

  const documentAction = async (id: string, operation: "complete" | "cancel") => {
    const question = operation === "complete"
      ? "Hoàn tất phiếu và cập nhật tồn linh kiện?"
      : "Hủy phiếu và giữ lịch sử bút toán đảo?";
    if (!window.confirm(question)) return;
    setError("");
    try {
      await apiRequest(`/api/inventory/documents/${id}/${operation}?scope=equipment`, { method: "POST" });
      setMessage(operation === "complete" ? "Đã cập nhật tồn linh kiện." : "Đã hủy phiếu linh kiện.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không thực hiện được thao tác.");
    }
  };

  const viewDocument = async (id: string) => {
    setError("");
    try {
      setDetail(await apiRequest<EquipmentDocumentDetail>(`/api/inventory/documents/${id}?scope=equipment`));
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không tải được chi tiết phiếu linh kiện.");
    }
  };

  if (loading && !foundation) return <LoadingState />;
  if (error && !foundation) return <ErrorState message={error} retry={load} />;

  const resultCount = mainTab === "Assets"
    ? visibleAssets.length
    : spareTab === "Catalog" ? visibleParts.length : spareTab === "Stock" ? visibleStock.length : visibleDocuments.length;

  return <div className="module-page equipment-page">
    <PageHeader
      eyebrow="THIẾT BỊ VÀ LINH KIỆN"
      title="Thiết bị"
      description="Quản lý thiết bị ở mức thông tin cơ bản; linh kiện nhập, xuất và tồn tại Kho vật tư khác bằng sổ kho dùng chung."
      action={mainTab === "Assets" && hasPermission("Equipment.Create")
        ? <button className="primary-button" onClick={() => { setEditingAssetId(""); setAssetForm(blankAsset()); }}><Plus size={18} /> Thêm thiết bị</button>
        : mainTab === "SpareParts" && spareTab === "Catalog" && hasPermission("Equipment.Create")
          ? <button className="primary-button" onClick={() => { setEditingPartId(""); setPartForm(blankPart(foundation?.uoms[0]?.id)); }}><Plus size={18} /> Thêm linh kiện</button>
          : mainTab === "SpareParts" && (spareTab === "Receipt" || spareTab === "Issue") && hasPermission("Equipment.Create")
            ? <button className="primary-button" onClick={() => openDocument(selectedDocumentType)}><PackagePlus size={18} /> Tạo phiếu {selectedDocumentType === "Receipt" ? "nhập" : "xuất"}</button>
            : undefined}
    />
    <Tabs tabs={[{ value: "Assets", label: "Thiết bị" }, { value: "SpareParts", label: "Linh kiện" }]} value={mainTab} onChange={(value) => { setMainTab(value as MainTab); setSearch(""); setMessage(""); }} />
    {mainTab === "SpareParts" && <Tabs tabs={[
      { value: "Catalog", label: "Danh mục linh kiện" }, { value: "Receipt", label: "Nhập linh kiện" },
      { value: "Issue", label: "Xuất linh kiện" }, { value: "Stock", label: "Tồn linh kiện" }
    ]} value={spareTab} onChange={(value) => { setSpareTab(value as SpareTab); setSearch(""); setMessage(""); }} />}
    {message && <Message tone="success">{message}</Message>}
    {error && <Message tone="error">{error}</Message>}
    <QuickSearch value={search} onChange={setSearch} scope="next" resultCount={resultCount}
      placeholder={mainTab === "Assets" ? "Tìm mã, tên, serial, vị trí hoặc quy cách thiết bị..." : spareTab === "Catalog" || spareTab === "Stock" ? "Tìm mã, tên, quy cách hoặc đơn vị linh kiện..." : "Tìm số phiếu, thiết bị, người nhận hoặc mục đích..."} />
    <section className="panel module-panel">
      {mainTab === "Assets" && <div className="data-table-wrapper"><table className="data-table"><thead><tr><th>STT</th><th>Mã thiết bị</th><th>Tên thiết bị</th><th>Quy cách / ký hiệu</th><th>Serial</th><th>Vị trí</th><th>Trạng thái</th><th /></tr></thead><tbody>
        {visibleAssets.map((row, index) => <tr key={row.id}><td>{index + 1}</td><td><strong>{row.code}</strong></td><td>{row.name}</td><td>{row.specificationSymbol || "—"}</td><td>{row.serialNumber || "—"}</td><td>{row.location || "—"}</td><td>{row.isActive ? "Đang dùng" : "Ngừng dùng"}</td><td>{hasPermission("Equipment.Edit") && <button className="table-action" onClick={() => { setEditingAssetId(row.id); setAssetForm({ code: row.code, name: row.name, specificationSymbol: row.specificationSymbol ?? "", serialNumber: row.serialNumber ?? "", location: row.location ?? "", notes: row.notes ?? "", isActive: row.isActive }); }}><Edit3 size={15} /> Sửa</button>}</td></tr>)}
        {!visibleAssets.length && <tr><td colSpan={8}><div className="empty-state">Chưa có thiết bị phù hợp bộ lọc.</div></td></tr>}
      </tbody></table></div>}
      {mainTab === "SpareParts" && spareTab === "Catalog" && <div className="data-table-wrapper"><table className="data-table"><thead><tr><th>STT</th><th>Mã linh kiện</th><th>Tên linh kiện</th><th>Đơn vị</th><th>Quy cách</th><th>Tồn tối thiểu</th><th>Trạng thái</th><th /></tr></thead><tbody>
        {visibleParts.map((row, index) => <tr key={row.id}><td>{index + 1}</td><td><strong>{row.code}</strong></td><td>{row.name}</td><td>{row.baseUom}</td><td>{row.specificationSymbol || "—"}</td><td>{row.trackMinimumStock ? `${formatNumber(row.minimumStockQuantity)} ${row.baseUom}` : "Không theo dõi"}</td><td>{row.isActive ? "Đang dùng" : "Ngừng dùng"}</td><td>{hasPermission("Equipment.Edit") && <button className="table-action" onClick={() => { setEditingPartId(row.id); setPartForm({ code: row.code, name: row.name, baseUomId: row.baseUomId, specificationSymbol: row.specificationSymbol ?? "", trackMinimumStock: row.trackMinimumStock, minimumStockQuantity: row.minimumStockQuantity ?? null, isActive: row.isActive }); }}><Edit3 size={15} /> Sửa</button>}</td></tr>)}
        {!visibleParts.length && <tr><td colSpan={8}><div className="empty-state">Chưa có linh kiện phù hợp bộ lọc.</div></td></tr>}
      </tbody></table></div>}
      {mainTab === "SpareParts" && (spareTab === "Receipt" || spareTab === "Issue") && <div className="data-table-wrapper"><table className="data-table"><thead><tr><th>STT</th><th>Số phiếu</th><th>Ngày</th><th>Mục đích</th><th>{selectedDocumentType === "Receipt" ? "Nhà cung cấp" : "Thiết bị"}</th><th>Người nhận</th><th>Số dòng</th><th>Giá trị</th><th>Trạng thái</th><th /></tr></thead><tbody>
        {visibleDocuments.map((row, index) => <tr key={row.id}><td>{index + 1}</td><td><strong>{row.number}</strong></td><td>{formatDate(row.documentDate)}</td><td>{purposeLabel(row.documentType, row.equipmentPurpose)}</td><td>{selectedDocumentType === "Receipt" ? row.partner?.name || "—" : row.equipment ? `${row.equipment.code} – ${row.equipment.name}` : "Không gắn thiết bị"}</td><td>{row.recipient || "—"}</td><td>{row.lineCount}</td><td>{row.totalAmount ? formatMoney(row.totalAmount) : "—"}</td><td><StatusBadge status={row.status} /></td><td><div className="table-actions"><button title="Xem" onClick={() => void viewDocument(row.id)}><Eye size={16} /></button>{row.status === "Draft" && hasPermission("Equipment.Edit") && <button title="Sửa" onClick={() => void openEditDocument(row.id)}><Edit3 size={16} /></button>}{row.status === "Draft" && hasPermission("Equipment.Complete") && <button className="success-action" title="Hoàn tất" onClick={() => void documentAction(row.id, "complete")}><Check size={16} /></button>}{row.status !== "Canceled" && hasPermission("Equipment.Cancel") && <button className="danger-action" title="Hủy" onClick={() => void documentAction(row.id, "cancel")}><RotateCcw size={16} /></button>}</div></td></tr>)}
        {!visibleDocuments.length && <tr><td colSpan={10}><div className="empty-state">Chưa có phiếu linh kiện phù hợp bộ lọc.</div></td></tr>}
      </tbody></table></div>}
      {mainTab === "SpareParts" && spareTab === "Stock" && <div className="data-table-wrapper"><table className="data-table numeric-table"><thead><tr><th>STT</th><th>Mã linh kiện</th><th>Tên linh kiện</th><th>Kho</th><th>Đơn vị</th><th className="number-cell">Tồn tối thiểu</th><th className="number-cell">Tồn hiện tại</th><th>Đánh giá</th></tr></thead><tbody>
        {visibleStock.map((row, index) => <tr key={row.itemId} className={row.isBelowMinimum ? "low-stock-row" : undefined}><td>{index + 1}</td><td><strong>{row.itemCode}</strong></td><td>{row.itemName}</td><td>{row.warehouseNames.join(", ") || foundation?.warehouse?.name || "KHO-VT"}</td><td>{row.uom}</td><td className="number-cell">{row.minimumStockQuantity == null ? "Không theo dõi" : formatNumber(row.minimumStockQuantity)}</td><td className="number-cell"><strong>{formatNumber(row.balance)}</strong></td><td>{row.minimumStockQuantity == null ? "—" : row.isBelowMinimum ? <span className="minimum-stock-status">Dưới mức tối thiểu</span> : "Đạt"}</td></tr>)}
        {!visibleStock.length && <tr><td colSpan={8}><div className="empty-state">Chưa có dữ liệu tồn linh kiện.</div></td></tr>}
      </tbody></table></div>}
    </section>

    {assetForm && <Modal title={editingAssetId ? "Sửa thiết bị" : "Thêm thiết bị"} subtitle="Giai đoạn này lưu thông tin nhận diện cơ bản; quy trình bảo trì sẽ bổ sung sau." onClose={() => setAssetForm(null)} wide error={error}><div className="form-grid three"><label>Mã thiết bị *<input required value={assetForm.code} onChange={(event) => setAssetForm({ ...assetForm, code: event.target.value })} /></label><label>Tên thiết bị *<input required value={assetForm.name} onChange={(event) => setAssetForm({ ...assetForm, name: event.target.value })} /></label><label>Serial<input value={assetForm.serialNumber} onChange={(event) => setAssetForm({ ...assetForm, serialNumber: event.target.value })} /></label><label>Quy cách / ký hiệu<input value={assetForm.specificationSymbol} onChange={(event) => setAssetForm({ ...assetForm, specificationSymbol: event.target.value })} /></label><label>Vị trí<input value={assetForm.location} onChange={(event) => setAssetForm({ ...assetForm, location: event.target.value })} /></label><label className="checkbox-label"><input type="checkbox" checked={assetForm.isActive} onChange={(event) => setAssetForm({ ...assetForm, isActive: event.target.checked })} /> Đang sử dụng</label></div><label className="full-field">Ghi chú<textarea value={assetForm.notes} onChange={(event) => setAssetForm({ ...assetForm, notes: event.target.value })} /></label><div className="form-actions"><button className="secondary-button" onClick={() => setAssetForm(null)}>Đóng</button><button className="primary-button" onClick={() => void saveAsset()}>Lưu thiết bị</button></div></Modal>}

    {partForm && <Modal title={editingPartId ? "Sửa linh kiện" : "Thêm linh kiện"} subtitle="Linh kiện được lưu riêng và nhập, xuất tại Kho vật tư khác." onClose={() => setPartForm(null)} wide error={error}><div className="form-grid three"><label>Mã linh kiện *<input required value={partForm.code} onChange={(event) => setPartForm({ ...partForm, code: event.target.value })} /></label><label>Tên linh kiện *<input required value={partForm.name} onChange={(event) => setPartForm({ ...partForm, name: event.target.value })} /></label><label>Đơn vị tính *<SearchSelect value={partForm.baseUomId} options={uomOptions} onChange={(baseUomId) => setPartForm({ ...partForm, baseUomId })} placeholder="Tìm đơn vị" /></label><label>Quy cách / ký hiệu<input value={partForm.specificationSymbol} onChange={(event) => setPartForm({ ...partForm, specificationSymbol: event.target.value })} /></label><label className="checkbox-label"><input type="checkbox" checked={partForm.trackMinimumStock} onChange={(event) => setPartForm({ ...partForm, trackMinimumStock: event.target.checked })} /> Theo dõi tồn tối thiểu</label><label>Mức tồn tối thiểu<input type="number" min="0.000001" step="any" disabled={!partForm.trackMinimumStock} required={partForm.trackMinimumStock} value={partForm.minimumStockQuantity ?? ""} onChange={(event) => setPartForm({ ...partForm, minimumStockQuantity: event.target.value === "" ? null : Number(event.target.value) })} /></label><label className="checkbox-label"><input type="checkbox" checked={partForm.isActive} onChange={(event) => setPartForm({ ...partForm, isActive: event.target.checked })} /> Đang sử dụng</label></div><div className="form-actions"><button className="secondary-button" onClick={() => setPartForm(null)}>Đóng</button><button className="primary-button" onClick={() => void savePart()}>Lưu linh kiện</button></div></Modal>}

    {documentForm && <Modal title={`${editingDocumentId ? "Sửa" : "Tạo"} phiếu ${documentForm.documentType === "Receipt" ? "nhập" : "xuất"} linh kiện`} subtitle={`Kho cố định: ${foundation?.warehouse?.code ?? "KHO-VT"} – ${foundation?.warehouse?.name ?? "Kho vật tư khác"}`} onClose={() => setDocumentForm(null)} wide error={error}><div className="form-grid three"><label>Số phiếu<input disabled={!!editingDocumentId} value={documentForm.number} onChange={(event) => setDocumentForm({ ...documentForm, number: event.target.value })} placeholder="Tự động" /></label><label>Ngày *<input type="date" required value={documentForm.documentDate} onChange={(event) => setDocumentForm({ ...documentForm, documentDate: event.target.value })} /></label><label>Mục đích *<select value={documentForm.equipmentPurpose} onChange={(event) => setDocumentForm({ ...documentForm, equipmentPurpose: event.target.value })}>{purposeOptions[documentForm.documentType].map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>{documentForm.documentType === "Receipt" ? <label>Nhà cung cấp<SearchSelect value={documentForm.partnerId} options={supplierOptions} onChange={(partnerId) => setDocumentForm({ ...documentForm, partnerId })} placeholder="Có thể để trống" /></label> : <label>Thiết bị {(documentForm.equipmentPurpose === "Repair" || documentForm.equipmentPurpose === "Maintenance") && "*"}<SearchSelect value={documentForm.equipmentId} options={equipmentOptions} onChange={(equipmentId) => setDocumentForm({ ...documentForm, equipmentId })} placeholder="Chọn thiết bị hoặc để trống khi cấp phát chung" /></label>}<label>Người nhận / người thực hiện<input value={documentForm.recipient} onChange={(event) => setDocumentForm({ ...documentForm, recipient: event.target.value })} /></label></div><div className="line-editor"><div className="line-editor-header"><strong>Danh sách linh kiện</strong></div><div className="data-table-wrapper"><table className="data-table line-table equipment-line-table"><thead><tr><th>STT</th><th>Linh kiện *</th><th>Đơn vị *</th><th>Số lượng *</th><th>Đơn giá</th><th>VAT</th><th>Thành giá</th><th /></tr></thead><tbody>{documentForm.lines.map((line, index) => { const subtotal = line.unitPrice === "" ? null : line.quantity * Number(line.unitPrice); const total = subtotal == null ? null : subtotal * (1 + Number(line.vatPercent || 0) / 100); return <tr key={line.clientKey}><td>{index + 1}</td><td><SearchSelect value={line.itemId} options={partOptions} onChange={(itemId) => updateDocumentLine(index, { itemId })} /></td><td><SearchSelect value={line.uomId} options={uomOptions} onChange={(uomId) => updateDocumentLine(index, { uomId })} placeholder="Tìm đơn vị" /></td><td><input type="number" min="0.000001" step="any" value={line.quantity} onChange={(event) => updateDocumentLine(index, { quantity: Number(event.target.value) })} /></td><td><input type="number" min="0" step="any" value={line.unitPrice} onChange={(event) => updateDocumentLine(index, { unitPrice: event.target.value })} placeholder="Có thể trống" /></td><td><select value={line.vatPercent} onChange={(event) => updateDocumentLine(index, { vatPercent: event.target.value })}><option value="">Trống</option><option value="0">0%</option><option value="5">5%</option><option value="8">8%</option><option value="10">10%</option></select></td><td className="number-cell">{total == null ? "—" : formatMoney(total)}</td><td><button className="remove-icon" disabled={documentForm.lines.length === 1} title="Xóa dòng" onClick={() => setDocumentForm({ ...documentForm, lines: documentForm.lines.filter((_, lineIndex) => lineIndex !== index) })}><Trash2 size={16} /></button></td></tr>; })}</tbody></table></div><div className="add-line-below"><button className="secondary-button" onClick={() => setDocumentForm({ ...documentForm, lines: [...documentForm.lines, blankLine()] })}><Plus size={16} /> Thêm dòng</button></div></div><label className="full-field">Ghi chú<textarea value={documentForm.notes} onChange={(event) => setDocumentForm({ ...documentForm, notes: event.target.value })} /></label><div className="form-actions"><button className="secondary-button" onClick={() => setDocumentForm(null)}>Đóng</button><button className="primary-button" onClick={() => void saveDocument()}>{editingDocumentId ? "Lưu thay đổi" : "Lưu bản Nháp"}</button></div></Modal>}

    {detail && <Modal title={detail.number} subtitle={detail.documentType === "Receipt" ? "Phiếu nhập linh kiện" : "Phiếu xuất linh kiện"} onClose={() => setDetail(null)} wide><div className="document-overview"><span>Ngày: <strong>{formatDate(detail.documentDate)}</strong></span><span>Kho: <strong>{detail.warehouseName || "Kho vật tư khác"}</strong></span><span>Mục đích: <strong>{purposeLabel(detail.documentType, detail.equipmentPurpose)}</strong></span><span>Thiết bị: <strong>{detail.equipment ? `${detail.equipment.code} – ${detail.equipment.name}` : "—"}</strong></span><span>Người nhận: <strong>{detail.recipient || "—"}</strong></span><span>Trạng thái: <StatusBadge status={detail.status} /></span></div><div className="data-table-wrapper"><table className="data-table"><thead><tr><th>STT</th><th>Mã linh kiện</th><th>Tên linh kiện</th><th>Đơn vị</th><th className="number-cell">Số lượng</th><th className="number-cell">Đơn giá</th><th className="number-cell">Thành tiền</th></tr></thead><tbody>{detail.lines.map((line) => <tr key={line.id}><td>{line.lineNo}</td><td><strong>{line.itemCode}</strong></td><td>{line.itemName}</td><td>{line.uomName}</td><td className="number-cell">{formatNumber(line.quantity)}</td><td className="number-cell">{line.unitPrice == null ? "—" : formatMoney(line.unitPrice)}</td><td className="number-cell">{line.lineTotal == null ? "—" : formatMoney(line.lineTotal)}</td></tr>)}</tbody></table></div>{detail.notes && <p><Wrench size={15} /> {detail.notes}</p>}</Modal>}
  </div>;
}
